import React, { useState } from "react";
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { ImportSummary, ImportResult, ImportValidationError } from "../../types/UserImport";

interface ImportPreviewProps {
  summary: ImportSummary;
  onRetry: () => void;
}

const ImportPreview: React.FC<ImportPreviewProps> = ({ summary, onRetry }) => {
  const [showErrors, setShowErrors] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(summary.results.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResults = summary.results.slice(startIndex, endIndex);

  const getStatusIcon = (result: ImportResult) => {
    if (result.success) {
      return <CheckCircle className="status-icon success" />;
    } else if (result.errors.length > 0) {
      return <ErrorIcon className="status-icon error" />;
    } else {
      return <Warning className="status-icon warning" />;
    }
  };

  const getStatusText = (result: ImportResult) => {
    if (result.success) {
      return result.warnings.length > 0 ? "Ready (with warnings)" : "Ready";
    } else {
      return `${result.errors.length} error(s)`;
    }
  };

  const getAllErrors = () => {
    const allErrors: (ImportValidationError & { userEmail: string })[] = [];
    summary.results.forEach((result, index) => {
      result.errors.forEach((error) => {
        allErrors.push({
          ...error,
          userEmail: result.user.email || `Row ${error.row}`,
        });
      });
    });
    return allErrors;
  };

  const formatRoles = (roles: string[]) => {
    return roles.length > 0 ? roles.join(", ") : "Not specified";
  };

  const formatBusinessUnits = (businessUnits: string[]) => {
    return businessUnits.length > 0 ? businessUnits.join(", ") : "Not specified";
  };

  return (
    <div className="preview-section">
      <div className="preview-header">
        <h3 className="preview-title">Import Preview</h3>
        <button className="action-button secondary cancel-button" onClick={onRetry}>
          Try Another File
        </button>
      </div>

      {/* Summary Statistics */}
      <div className="summary-stats">
        <div className="stat-item">
          <div className="stat-value">{summary.totalRows}</div>
          <div className="stat-label">Total Rows</div>
        </div>
        <div className="stat-item">
          <div className="stat-value success">{summary.successCount}</div>
          <div className="stat-label">Ready to Import</div>
        </div>
        <div className="stat-item">
          <div className="stat-value error">{summary.errorCount}</div>
          <div className="stat-label">Errors</div>
        </div>
        <div className="stat-item">
          <div className="stat-value warning">{summary.warningCount}</div>
          <div className="stat-label">Warnings</div>
        </div>
      </div>

      {/* Duplicate Emails Warning */}
      {summary.duplicateEmails.length > 0 && (
        <div className="error-list">
          <div className="error-item">
            <Warning className="error-icon" />
            <span>
              Duplicate emails found: {summary.duplicateEmails.join(", ")}. Only the first
              occurrence will be imported.
            </span>
          </div>
        </div>
      )}

      {/* Error Summary */}
      {summary.errorCount > 0 && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              marginBottom: "10px",
            }}
            onClick={() => setShowErrors(!showErrors)}
          >
            <h4 style={{ margin: 0, color: "#f44336" }}>{summary.errorCount} Error(s) Found</h4>
            {showErrors ? <ExpandLess /> : <ExpandMore />}
          </div>

          {showErrors && (
            <div className="error-list">
              {getAllErrors()
                .slice(0, 20)
                .map((error, index) => (
                  <div key={index} className="error-item">
                    <ErrorIcon className="error-icon" />
                    <span>
                      <strong>{error.userEmail}</strong> - {error.field}: {error.message}
                    </span>
                  </div>
                ))}
              {getAllErrors().length > 20 && (
                <div className="error-item">
                  <span>... and {getAllErrors().length - 20} more errors</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Preview Table */}
      <table className="preview-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Name</th>
            <th>Email</th>
            <th>Roles</th>
            <th>Clients</th>
            <th>Active</th>
          </tr>
        </thead>
        <tbody>
          {currentResults.map((result, index) => (
            <tr key={startIndex + index}>
              <td>
                <div className="row-status">
                  {getStatusIcon(result)}
                  <span style={{ fontSize: "11px" }}>{getStatusText(result)}</span>
                </div>
              </td>
              <td title={`${result.user.firstName} ${result.user.lastName}`}>
                {result.user.firstName} {result.user.lastName}
              </td>
              <td title={result.user.email}>{result.user.email}</td>
              <td title={formatRoles(result.user.roles)}>{formatRoles(result.user.roles)}</td>
              <td title={formatBusinessUnits(result.user.businessUnits)}>
                {formatBusinessUnits(result.user.businessUnits)}
              </td>
              <td>
                <span
                  style={{
                    color: result.user.isActive ? "#4caf50" : "#f44336",
                    fontWeight: 500,
                  }}
                >
                  {result.user.isActive ? "Yes" : "No"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            className="action-button secondary"
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            style={{ padding: "8px 12px", fontSize: "12px" }}
          >
            Previous
          </button>

          <span style={{ fontSize: "14px", color: "#666" }}>
            Page {currentPage + 1} of {totalPages}
          </span>

          <button
            className="action-button secondary"
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage === totalPages - 1}
            style={{ padding: "8px 12px", fontSize: "12px" }}
          >
            Next
          </button>
        </div>
      )}

      {/* Import Summary */}
      <div
        style={{
          marginTop: "20px",
          padding: "15px",
          backgroundColor: summary.successCount > 0 ? "#f1f8e9" : "#fff3e0",
          borderRadius: "8px",
          border: `1px solid ${summary.successCount > 0 ? "#c8e6c9" : "#ffcc02"}`,
        }}
      >
        <p style={{ margin: 0, fontSize: "14px", color: "#424242" }}>
          {summary.successCount > 0 ? (
            <>
              <strong>{summary.successCount}</strong> users are ready to be imported.
              {summary.errorCount > 0 && (
                <>
                  {" "}
                  <strong>{summary.errorCount}</strong> users have errors and will be skipped.
                </>
              )}
              {summary.warningCount > 0 && (
                <>
                  {" "}
                  There are <strong>{summary.warningCount}</strong> warnings that you should review.
                </>
              )}
            </>
          ) : (
            <>
              No users can be imported due to validation errors. Please fix the errors in your Excel
              file and try again.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default ImportPreview;
